/**
 * NutritionCreateWizard — the create-client flow for NUTRITION clinics.
 *
 * A full replacement, not a re-skin: for a nutrition clinic the classic wizard
 * is unreachable (CreatePatientPage branches on selectIsNutritionClinic). The
 * classic wizard is untouched for every other clinic type.
 *
 * Five ordered tabs, freely clickable, with "המשך" walking them in order:
 *   1. פרטי לקוח            — the only gated step (name + phone)
 *   2. פרטים אישיים ומדדים  — gender + birth date FIRST (they unlock every
 *                             calculation), then body measurements with live,
 *                             explained BMI / BMR / body-fat
 *   3. יעדים                — behind an overlay; skipping is explicit and
 *                             remembered (workspace banner + pending queue)
 *   4. הגדרות ליווי         — client-page switches (all ON), reminders
 *   5. סיום                 — created → the personal-page link, ready to send
 *
 * Shell classes (pat-wiz-*) are shared with the classic wizard on purpose:
 * the two must look like one product. Nutrition-only pieces are nwz-*.
 * `.form-container` stays — the onboarding tour anchors to it.
 */

import { useState } from "react";
import { useSelector } from "react-redux";
import {
  ArrowLeft,
  ArrowRight,
  ArrowSquareOut,
  Barbell,
  CalendarCheck,
  ChartDonut,
  Check,
  CheckCircle,
  ForkKnife,
  Info,
  Ruler,
  PencilSimple,
  Sparkle,
  Target,
  TrendUp,
  User,
  UserCircle,
  UserPlus,
  UsersThree,
  Warning,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import ModalCloseButton from "../../../ui/ModalCloseButton";
import ConfirmDialog from "../../../ui/ConfirmDialog";
import DatePicker from "../../../ui/DatePicker";
import TimePicker from "../../../ui/TimePicker";
import { GlobalLoader, HelpButton } from "../../../shared";
import { NoAccessState } from "../../../shared/access";
import { selectHasPermission } from "../../../../store/slices/authSlice";
import EnglishCommunicationToggle from "../../EnglishCommunicationToggle";
import GenderSelect from "../GenderSelect";
import MacroIcon from "../../../nutrition/MacroIcon";
import PersonalPageLink from "../../nutrition/PersonalPageLink";
import GoalSuggestion from "../../nutrition/GoalSuggestion";
import { MEASUREMENT_FIELDS } from "../../../../firebase";
import HoverTip from "../../../ui/HoverTip";
import {
  ACTIVITY_LEVEL_LABELS,
  DIET_PLANS,
  GOAL_TYPES,
  METRIC_OVERRIDE_FIELDS,
  macrosFromDietPlan,
} from "../../../../utils/nutritionCalc";
import useNutritionPatientForm, { NUTRITION_STEPS } from "./useNutritionPatientForm";
import "../CreatePatientWizard.css";
import "../../PatientToggles.css";
import "./NutritionCreateWizard.css";

// ============================================================================
// Constants
// ============================================================================

const TABS = [
  { id: NUTRITION_STEPS.CLIENT, label: "פרטי לקוח", icon: User, required: true },
  { id: NUTRITION_STEPS.BODY, label: "פרטים ומדדים", icon: Ruler },
  { id: NUTRITION_STEPS.GOALS, label: "יעדים", icon: Target },
  { id: NUTRITION_STEPS.COACHING, label: "הגדרות ליווי", icon: Sparkle },
  { id: NUTRITION_STEPS.FINISH, label: "סיום", icon: CheckCircle },
];

/** Short "why this matters" line at the top of each step. */
const STEP_INTRO = {
  [NUTRITION_STEPS.CLIENT]:
    "שם ומספר טלפון מספיקים כדי ליצור לקוח/ה. הטלפון הוא גם המפתח לעמוד האישי — הכניסה אליו מאומתת ב-4 הספרות האחרונות.",
  [NUTRITION_STEPS.BODY]:
    "מגדר וגיל הם מה שמאפשר למערכת לחשב. יחד עם משקל וגובה תראו כאן, בזמן אמת, BMI, קצב חילוף חומרים ואחוז שומן — עם הסבר לכל מספר.",
  [NUTRITION_STEPS.GOALS]:
    "היעדים היומיים הם מה שהלקוח/ה רואה מול הדיווחים שלו/ה. אפשר להגדיר עכשיו, ואפשר גם אחר כך — המערכת תזכיר.",
  [NUTRITION_STEPS.COACHING]:
    "מה הלקוח/ה יכול/ה לעשות בעמוד האישי, ואיך המערכת מזכירה למדוד. הכול דולק כברירת מחדל — כבו רק מה שלא מתאים.",
};

const MACRO_FIELDS = [
  { key: "calories", label: "קלוריות", unit: "קק״ל" },
  { key: "carbohydrates", label: "פחמימות", unit: "ג׳" },
  { key: "protein", label: "חלבון", unit: "ג׳" },
  { key: "fat", label: "שומן", unit: "ג׳" },
];

const FEATURE_TOGGLES = [
  {
    key: "selfLoggingEnabled",
    icon: ForkKnife,
    title: "דיווח אכילה עצמאי",
    sub: "הלקוח/ה מדווח/ת מה אכל/ה בעמוד האישי, והדוח מגיע אליכם לסקירה",
  },
  {
    key: "aiEstimateEnabled",
    icon: Sparkle,
    title: "פירוק ארוחה אוטומטי (AI)",
    sub: "טקסט חופשי כמו ״2 פרוסות לחם עם גבינה״ הופך לערכים תזונתיים שאפשר לתקן",
  },
  {
    key: "selfMeasurementsEnabled",
    icon: Ruler,
    title: "מדידות עצמאיות",
    sub: "הלקוח/ה מוסיף/ה משקל והיקפים בעצמו/ה — הגרפים מתעדכנים אצלכם",
  },
  {
    key: "showGoalsToClient",
    icon: Target,
    title: "הצגת היעדים ללקוח/ה",
    sub: "הסיכום היומי בעמוד האישי מוצג מול היעד שהגדרתם",
  },
];

const FREQUENCIES = [
  { key: "weekly", label: "שבועי" },
  { key: "biweekly", label: "דו-שבועי" },
  { key: "monthly", label: "חודשי" },
];

const WEEKDAYS = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"];

/** The measurement inputs shown at intake, in the order that reads naturally. */
const INTAKE_MEASUREMENTS = ["weight", "height", "fatPercentage", "waist", "neck", "hip", "arm", "thigh", "bust"];

const BMI_BANDS = [
  { max: 18.5, label: "תת-משקל" },
  { max: 25, label: "טווח תקין" },
  { max: 30, label: "עודף משקל" },
  { max: Infinity, label: "השמנה" },
];

const bmiBand = (bmi) => BMI_BANDS.find((band) => bmi < band.max)?.label || "";

// ============================================================================
// Small pieces
// ============================================================================

function StepIntro({ step }) {
  const text = STEP_INTRO[step];
  if (!text) return null;
  return (
    <div className="nwz-intro">
      <Info size={15} weight="bold" />
      <span>{text}</span>
    </div>
  );
}

/**
 * One live, explained number on the body step, with a manual override
 * (Round 3 / Phase 20): "עריכה ידנית" turns the value into an input; the
 * computed value keeps living underneath and "בטל" restores it.
 *
 * `override`: { value, onChange(value|null), min, max, step, inputUnit }
 */
function MetricTile({ icon: Icon, label, value, unit, explain, missing, override }) {
  const isManual = override?.value !== null && override?.value !== undefined && override?.value !== "";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const isReady = value !== null && value !== undefined;

  // Leaving edit mode with an empty draft means "no override".
  const commit = () => {
    const trimmed = String(draft).trim();
    override.onChange(trimmed === "" ? null : trimmed);
    setEditing(false);
  };
  const cancelOverride = () => {
    override.onChange(null);
    setDraft("");
    setEditing(false);
  };
  const startEditing = () => {
    setDraft(isManual ? String(override.value) : isReady ? String(value) : "");
    setEditing(true);
  };

  return (
    <div className={`nwz-metric ${isReady ? "ready" : "waiting"} ${isManual ? "manual" : ""}`}>
      <span className="nwz-metric-head">
        {Icon ? <Icon size={14} weight="bold" /> : null}
        {label}
        {isManual && (
          <HoverTip text="הערך הוזן ידנית ולא יתעדכן כשמשנים את המדידות. הערך המחושב נשמר ברקע.">
            <span className="nwz-metric-badge">הוזן ידנית</span>
          </HoverTip>
        )}
      </span>

      {editing ? (
        <span className="nwz-metric-edit">
          <input
            type="number"
            inputMode="decimal"
            dir="ltr"
            lang="en"
            min={override.min}
            max={override.max}
            step={override.step}
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              }
              if (e.key === "Escape") setEditing(false);
            }}
            aria-label={`${label} — הזנה ידנית`}
          />
          {override.inputUnit && <span className="nwz-metric-unit">{override.inputUnit}</span>}
        </span>
      ) : (
        <span className="nwz-metric-value">
          {isReady ? (
            <>
              <strong>{value}</strong>
              {unit && <span className="nwz-metric-unit">{unit}</span>}
            </>
          ) : (
            <span className="nwz-metric-missing">{missing}</span>
          )}
        </span>
      )}
      <span className="nwz-metric-explain">{explain}</span>

      {override && (
        <span className="nwz-metric-actions">
          {editing ? (
            <>
              <button type="button" className="nwz-metric-btn primary" onClick={commit}>
                <Check size={12} weight="bold" /> אישור
              </button>
              <button type="button" className="nwz-metric-btn" onClick={() => setEditing(false)}>
                סגירה
              </button>
            </>
          ) : (
            <>
              <HoverTip text="הזינו ערך משלכם (למשל ממכשיר מדידה). הערך המחושב נשמר ברקע ואפשר לחזור אליו.">
                <button type="button" className="nwz-metric-btn" onClick={startEditing}>
                  <PencilSimple size={12} weight="bold" /> עריכה ידנית
                </button>
              </HoverTip>
              {isManual && (
                <HoverTip text="חזרה לערך המחושב אוטומטית">
                  <button type="button" className="nwz-metric-btn" onClick={cancelOverride}>
                    בטל
                  </button>
                </HoverTip>
              )}
            </>
          )}
        </span>
      )}
    </div>
  );
}

// ============================================================================
// Step 1 — פרטי לקוח
// ============================================================================

function ClientStep({ form }) {
  const {
    formData,
    errors,
    handleChange,
    formatPhone,
    englishCommunicationEnabled,
    canAssignToOtherTherapist,
    therapistRequired,
    activeTherapists,
    selectedTherapistId,
    setSelectedTherapistId,
    therapistId,
  } = form;

  return (
    <div className="pat-wiz-step-panel" key="step-client">
      <StepIntro step={NUTRITION_STEPS.CLIENT} />

      {canAssignToOtherTherapist &&
        (activeTherapists.length > 1 || therapistRequired) && (
        <div className="form-group">
          <label className={therapistRequired ? "required" : ""}>
            <UsersThree size={16} weight="bold" /> שיוך למאמן/ת
          </label>
          <select
            value={selectedTherapistId || ""}
            onChange={(e) => setSelectedTherapistId(e.target.value)}
            className={errors.therapistId ? "error" : ""}
          >
            {therapistRequired && <option value="">בחר/י מאמן/ת</option>}
            {activeTherapists.map((t) => (
              <option key={t.uid} value={t.uid}>
                {t.firstName} {t.lastName}
                {t.uid === therapistId ? " (אני)" : ""}
              </option>
            ))}
          </select>
          {errors.therapistId && <span className="error-text">{errors.therapistId}</span>}
        </div>
      )}

      <div className="form-row">
        <div className="form-group">
          <label className="required">שם פרטי</label>
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => handleChange("firstName", e.target.value)}
            className={errors.firstName ? "error" : ""}
            placeholder="שם פרטי"
            autoFocus
          />
          {errors.firstName && <span className="error-text">{errors.firstName}</span>}
        </div>
        <div className="form-group">
          <label className="required">שם משפחה</label>
          <input
            type="text"
            value={formData.lastName}
            onChange={(e) => handleChange("lastName", e.target.value)}
            className={errors.lastName ? "error" : ""}
            placeholder="שם משפחה"
          />
          {errors.lastName && <span className="error-text">{errors.lastName}</span>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="required">מספר טלפון נייד</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange("phone", formatPhone(e.target.value))}
            className={errors.phone ? "error" : ""}
            placeholder="0501234567"
            dir="ltr"
          />
          {errors.phone && <span className="error-text">{errors.phone}</span>}
        </div>
        <div className="form-group">
          <label>אימייל</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            placeholder="email@example.com"
            dir="ltr"
          />
        </div>
      </div>

      {englishCommunicationEnabled && (
        <div className="ptg-grid">
          <EnglishCommunicationToggle
            language={formData.communicationLanguage}
            onChange={(value) => handleChange("communicationLanguage", value)}
          />
        </div>
      )}

      <div className="form-group">
        <label>סיבת הפנייה</label>
        <textarea
          value={formData.referralReason}
          onChange={(e) => handleChange("referralReason", e.target.value)}
          placeholder="למשל: ירידה במשקל לקראת אירוע, איזון סוכר, בניית מסת שריר..."
          rows={3}
        />
        <span className="field-hint">מה הלקוח/ה רוצה להשיג — יופיע בכרטיס הלקוח/ה</span>
      </div>
    </div>
  );
}

// ============================================================================
// Step 2 — פרטים אישיים ומדדים
// ============================================================================

function BodyStep({ form }) {
  const { formData, handleChange, setMeasurementField, setMetricOverride, bodyMetrics } = form;
  // clinical.viewSensitive: only an explicit false hides the medical field
  const canViewSensitive = useSelector(selectHasPermission("clinical.viewSensitive"));
  const measurement = formData.initialMeasurement || {};
  const overrides = formData.metricOverrides || {};
  const {
    age,
    gender,
    bmi,
    bmr,
    fatPercentage,
    fatIsEstimated,
    fatIsManual,
    bmiIsManual,
    bmrIsManual,
    hasWeight,
    hasHeight,
  } = bodyMetrics;

  const bodyDataMissing = [];
  if (!gender) bodyDataMissing.push("מגדר");
  if (!age) bodyDataMissing.push("תאריך לידה");
  if (!hasWeight) bodyDataMissing.push("משקל");
  if (!hasHeight) bodyDataMissing.push("גובה");

  return (
    <div className="pat-wiz-step-panel" key="step-body">
      <StepIntro step={NUTRITION_STEPS.BODY} />

      <h3 className="section-title">פרטים אישיים</h3>
      <div className="form-row">
        <div className="form-group">
          <label>מגדר</label>
          <GenderSelect value={formData.gender} onChange={(value) => handleChange("gender", value)} />
          <span className="field-hint">נדרש לנוסחאות BMR ואחוז השומן</span>
        </div>
        <div className="form-group">
          <label>תאריך לידה</label>
          <DatePicker
            value={formData.birthDate}
            onChange={(e) => handleChange("birthDate", e.target.value)}
          />
          <span className="field-hint">
            {age ? `גיל ${age} — נכנס לחישוב ה-BMR` : "הגיל נכנס לחישוב ה-BMR"}
          </span>
        </div>
      </div>

      {canViewSensitive === false ? (
        <NoAccessState size="inline" message="מידע רפואי — אין הרשאה" />
      ) : (
        <div className="form-group">
          <label>רקע רפואי</label>
          <textarea
            value={formData.clinicalNotes}
            onChange={(e) => handleChange("clinicalNotes", e.target.value)}
            placeholder="מחלות רקע, תרופות, רגישויות, אלרגיות, ניתוחים..."
            rows={3}
          />
          <span className="field-hint">שדה חופשי אחד. אפשר לפרט יותר מאוחר יותר בכרטיס הלקוח/ה</span>
        </div>
      )}

      <h3 className="section-title">מדידות פתיחה</h3>
      <div className="nwz-measure-grid">
        {INTAKE_MEASUREMENTS.map((field) => {
          const spec = MEASUREMENT_FIELDS[field];
          if (!spec) return null;
          return (
            <div className="form-group" key={field}>
              <label>
                {spec.label} <span className="nwz-unit">({spec.unit})</span>
              </label>
              <input
                type="number"
                min={spec.min}
                max={spec.max}
                step="0.1"
                inputMode="decimal"
                dir="ltr"
                lang="en"
                value={measurement[field] ?? ""}
                onChange={(e) => setMeasurementField(field, e.target.value)}
                placeholder="—"
              />
            </div>
          );
        })}
      </div>
      <span className="field-hint">
        לא חובה. מה שימולא נשמר כמדידה הראשונה של הלקוח/ה — נקודת ההשוואה לכל מה שיבוא.
      </span>

      <h3 className="section-title">
        <Sparkle size={15} weight="bold" /> מה המערכת מחשבת
      </h3>
      <div className="nwz-metrics">
        <MetricTile
          label="BMI"
          value={bmi}
          unit={bmi ? bmiBand(bmi) : ""}
          missing={!hasWeight || !hasHeight ? "צריך משקל וגובה" : ""}
          explain={
            bmiIsManual
              ? "הוזן ידנית. מדד מסת גוף — מסווג לטווחים (תקין 18.5–25)."
              : "מדד מסת גוף: משקל חלקי גובה בריבוע. מסווג לטווחים (תקין 18.5–25) ולא מבדיל בין שומן לשריר."
          }
          override={{
            value: overrides.bmi,
            onChange: (v) => setMetricOverride("bmi", v),
            min: METRIC_OVERRIDE_FIELDS.bmi.min,
            max: METRIC_OVERRIDE_FIELDS.bmi.max,
            step: "0.1",
          }}
        />
        <MetricTile
          label="BMR"
          value={bmr}
          unit="קק״ל ליום"
          missing={bodyDataMissing.length ? `צריך ${bodyDataMissing.join(", ")}` : ""}
          explain={
            bmrIsManual
              ? "הוזן ידנית. קצב חילוף חומרים במנוחה — הבסיס ליעד הקלוריות."
              : "קצב חילוף חומרים במנוחה (Mifflin-St Jeor): כמה קלוריות הגוף שורף ביום בלי פעילות. הבסיס ליעד הקלוריות."
          }
          override={{
            value: overrides.bmr,
            onChange: (v) => setMetricOverride("bmr", v),
            min: METRIC_OVERRIDE_FIELDS.bmr.min,
            max: METRIC_OVERRIDE_FIELDS.bmr.max,
            step: "1",
            inputUnit: "קק״ל",
          }}
        />
        <MetricTile
          label={fatIsEstimated ? "אחוז שומן (הערכה)" : "אחוז שומן"}
          value={fatPercentage}
          unit="%"
          missing={!gender || !hasHeight ? "צריך מגדר וגובה" : "צריך היקף מותניים וצוואר, או ערך מדוד"}
          explain={
            fatIsEstimated
              ? "משוער מהיקף המותניים והצוואר ומהגובה (נוסחת US Navy). ערך שנמדד במכשיר תמיד גובר על ההערכה."
              : "הערך שהוזן. אם אין מכשיר, המערכת תעריך מהיקף המותניים והצוואר."
          }
          override={{
            // A manual body-fat value IS the measured field — same "measured wins" rule.
            value: fatIsManual ? measurement.fatPercentage : null,
            onChange: (v) => setMeasurementField("fatPercentage", v ?? ""),
            min: MEASUREMENT_FIELDS.fatPercentage.min,
            max: MEASUREMENT_FIELDS.fatPercentage.max,
            step: "0.1",
            inputUnit: "%",
          }}
        />
      </div>
    </div>
  );
}

// ============================================================================
// Step 3 — יעדים
// ============================================================================

function GoalsStep({ form }) {
  const {
    formData,
    setNutrition,
    setGoal,
    bodyMetrics,
    goalSuggestions,
    goalsRevealed,
    setGoalsRevealed,
    goalsSkipped,
    setGoalsSkipped,
    goalsAreSet,
  } = form;
  const nutrition = formData.nutrition || {};
  const goals = nutrition.goals || {};

  const applyDietPlan = (planKey) => {
    const macros = macrosFromDietPlan(Number(goals.calories) || 0, planKey);
    setNutrition({ goals: { ...goals, dietPlan: planKey, ...(macros || {}) } });
  };

  const reveal = () => {
    setGoalsRevealed(true);
    setGoalsSkipped(false);
  };

  return (
    <div className="pat-wiz-step-panel" key="step-goals">
      <StepIntro step={NUTRITION_STEPS.GOALS} />

      <div className="nwz-goals-wrap">
        {!goalsRevealed && (
          <div className="nwz-goals-overlay">
            <span className="nwz-goals-overlay-icon">
              <Target size={24} weight="fill" />
            </span>
            <h4>יעדים יומיים ללקוח/ה</h4>
            <p>
              כאן מגדירים כמה קלוריות, פחמימות, חלבון ושומן הלקוח/ה אמור/ה לצרוך ביום.
              היעדים מוצגים לו/ה בעמוד האישי מול הדיווחים, ואפשר לערוך אותם בכל רגע.
            </p>
            <button type="button" className="pat-wiz-btn primary" onClick={reveal}>
              <Target size={16} weight="bold" />
              אני רוצה להגדיר
            </button>
            <button
              type="button"
              className="nwz-skip-btn"
              onClick={() => setGoalsSkipped(true)}
            >
              אין לי עדיין את המספרים — נדלג בינתיים
            </button>
            {goalsSkipped && (
              <span className="nwz-skip-note">
                <Warning size={13} weight="fill" />
                בסדר גמור. אחרי היצירה תופיע תזכורת במרכז הליווי וברשימת הפעולות הממתינות עד שהיעדים יוגדרו.
              </span>
            )}
          </div>
        )}

        <div className={`nwz-goals-body ${goalsRevealed ? "" : "covered"}`} aria-hidden={!goalsRevealed}>
          <div className="form-row">
            <div className="form-group">
              <label>
                <TrendUp size={15} weight="bold" /> מטרת הליווי
              </label>
              <select
                value={nutrition.goalType || "maintain"}
                onChange={(e) => setNutrition({ goalType: e.target.value })}
                tabIndex={goalsRevealed ? 0 : -1}
              >
                {Object.entries(GOAL_TYPES).map(([key, { label }]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
              <span className="field-hint">קובע אם ההמלצה לקלוריות תהיה מתחת, מעל או על ההוצאה היומית</span>
            </div>
            <div className="form-group">
              <label>
                <Barbell size={15} weight="bold" /> רמת פעילות
              </label>
              <select
                value={nutrition.activityLevel || ""}
                onChange={(e) => setNutrition({ activityLevel: e.target.value || null })}
                tabIndex={goalsRevealed ? 0 : -1}
              >
                <option value="">לא הוגדר</option>
                {Object.entries(ACTIVITY_LEVEL_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
              <span className="field-hint">מכפיל את ה-BMR להוצאה יומית משוערת (TDEE)</span>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                <ChartDonut size={15} weight="bold" /> תוכנית תזונה
              </label>
              <select
                value={goals.dietPlan || ""}
                onChange={(e) => applyDietPlan(e.target.value)}
                tabIndex={goalsRevealed ? 0 : -1}
              >
                <option value="">לא הוגדר</option>
                {DIET_PLANS.map((plan) => (
                  <option key={plan.key} value={plan.key}>
                    {plan.label}
                  </option>
                ))}
              </select>
              <span className="field-hint">ממלאת חלבון / פחמימות / שומן מתוך יעד הקלוריות</span>
            </div>
          </div>

          {bodyMetrics.bmr && (
            <div className="nwz-guidance">
              <Info size={14} weight="bold" />
              <span>
                להערכה בלבד: BMR של כ-{bodyMetrics.bmr} קק״ל
                {bodyMetrics.tdee
                  ? `, והוצאה יומית משוערת של כ-${bodyMetrics.tdee} קק״ל לפי רמת הפעילות שנבחרה`
                  : " — בחרו רמת פעילות כדי לראות הוצאה יומית משוערת"}
                . יעד לירידה במשקל בדרך כלל נמוך מההוצאה היומית; יעד לעלייה — גבוה ממנה.
              </span>
            </div>
          )}

          <div className="nwz-goals-grid">
            {MACRO_FIELDS.map(({ key, label, unit }) => (
              <div className="form-group" key={key}>
                <label>
                  <MacroIcon macro={key} size={14} />
                  {label} <span className="nwz-unit">({unit})</span>
                </label>
                <input
                  type="number"
                  min="0"
                  inputMode="numeric"
                  dir="ltr"
                  lang="en"
                  value={goals[key] ?? ""}
                  onChange={(e) => setGoal(key, e.target.value === "" ? null : Number(e.target.value))}
                  placeholder="—"
                  tabIndex={goalsRevealed ? 0 : -1}
                />
                <GoalSuggestion suggestion={goalSuggestions?.[key]} />
              </div>
            ))}
          </div>

          {goalsRevealed && !goalsAreSet && (
            <span className="field-hint">
              עדיין לא הוזן יעד. אם תמשיכו בלי, המערכת תזכיר אחרי היצירה.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Step 4 — הגדרות ליווי
// ============================================================================

function CoachingStep({ form }) {
  const { formData, setNutrition, setSchedule } = form;
  const nutrition = formData.nutrition || {};
  const schedule = nutrition.measurementSchedule || {};

  return (
    <div className="pat-wiz-step-panel" key="step-coaching">
      <StepIntro step={NUTRITION_STEPS.COACHING} />

      <div className="ptg-grid">
        {FEATURE_TOGGLES.map(({ key, icon: Icon, title, sub }) => {
          const isOn = nutrition[key] !== false;
          return (
            <label key={key} className={`ptg-card ${isOn ? "is-on" : ""}`}>
              <span className="ptg-icon">
                <Icon size={17} weight={isOn ? "fill" : "duotone"} />
              </span>
              <span className="ptg-text">
                <span className="ptg-title">{title}</span>
                <span className="ptg-sub">{sub}</span>
              </span>
              <span className="ptg-switch">
                <input
                  type="checkbox"
                  checked={isOn}
                  onChange={(e) => setNutrition({ [key]: e.target.checked })}
                />
                <span className="ptg-slider"></span>
              </span>
            </label>
          );
        })}
      </div>

      <h3 className="section-title">
        <CalendarCheck size={15} weight="bold" /> תזכורת מדידות
      </h3>

      <label className={`ptg-card ${schedule.enabled === true ? "is-on" : ""}`}>
        <span className="ptg-icon">
          <Ruler size={17} weight={schedule.enabled === true ? "fill" : "duotone"} />
        </span>
        <span className="ptg-text">
          <span className="ptg-title">מעקב מדידות קבוע</span>
          <span className="ptg-sub">
            כשמגיע המועד, הלקוח/ה יופיע/תופיע אצלכם ברשימת הפעולות הממתינות עם כפתור לשליחת תזכורת
          </span>
        </span>
        <span className="ptg-switch">
          <input
            type="checkbox"
            checked={schedule.enabled === true}
            onChange={(e) => setSchedule("enabled", e.target.checked)}
          />
          <span className="ptg-slider"></span>
        </span>
      </label>

      {schedule.enabled === true && (
        <div className="nwz-schedule-grid">
          <div className="form-group">
            <label>תדירות</label>
            <select
              value={schedule.frequency || "weekly"}
              onChange={(e) => setSchedule("frequency", e.target.value)}
            >
              {FREQUENCIES.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>יום בשבוע</label>
            <select
              value={schedule.weekday ?? 0}
              onChange={(e) => setSchedule("weekday", Number(e.target.value))}
            >
              {WEEKDAYS.map((day, i) => (
                <option key={day} value={i}>
                  יום {day}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>שעה</label>
            <TimePicker
              value={schedule.time || "08:00"}
              onChange={(e) => setSchedule("time", e?.target ? e.target.value : e)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Step 5 — סיום + הקישור
// ============================================================================

function FinishStep({ form }) {
  const navigate = useNavigate();
  const { clinicId, createdPatient, goalsAreSet } = form;
  if (!createdPatient) return null;

  const name = `${createdPatient.firstName || ""} ${createdPatient.lastName || ""}`.trim();

  return (
    <div className="pat-wiz-step-panel" key="step-finish">
      <div className="nwz-done">
        <span className="nwz-done-icon">
          <CheckCircle size={28} weight="fill" />
        </span>
        <h4>{name} נוצר/ה בהצלחה</h4>
        <p>
          זה הקישור לעמוד האישי של הלקוח/ה. בעמוד הזה הוא/היא רואה/ה את התפריט הפעיל,
          מדווח/ת מה אכל/ה ומוסיף/ה מדידות — גם לפני שבניתם תפריט. הכניסה מאומתת
          ב-4 הספרות האחרונות של הטלפון.
        </p>
      </div>

      <PersonalPageLink clinicId={clinicId} patient={createdPatient} variant="card" />

      {!goalsAreSet && (
        <div className="nwz-reminder">
          <Warning size={15} weight="fill" />
          <span>
            לא הוגדרו יעדים יומיים. תזכורת תחכה לכם במרכז הליווי וברשימת הפעולות הממתינות עד
            שתגדירו אותם.
          </span>
        </div>
      )}

      <div className="nwz-next">
        <span className="nwz-next-title">מה עכשיו?</span>
        <div className="nwz-next-actions">
          <button
            type="button"
            className="pat-wiz-btn primary"
            onClick={() => navigate(`/dashboard/nutrition/${createdPatient.id}`)}
          >
            <ArrowSquareOut size={16} weight="bold" />
            פתח/י את מרכז הליווי — לבניית תפריט ראשון
          </button>
          <button
            type="button"
            className="pat-wiz-btn secondary"
            onClick={() => navigate(`/dashboard/patients/${createdPatient.id}`)}
          >
            <UserCircle size={16} weight="bold" />
            לכרטיס הלקוח/ה
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Wizard shell
// ============================================================================

function NutritionCreateWizard() {
  const form = useNutritionPatientForm();
  const {
    activeStep,
    goToStep,
    goNext,
    goBack,
    errors,
    canSave,
    createdPatient,
    handleSubmit,
    handleSafeClose,
    handleDiscardChanges,
    showCloseWarning,
    setShowCloseWarning,
    isLoading,
    isChecking,
    saveDisabled,
    PlanLimitModalComponent,
  } = form;

  const isLastFormStep = activeStep === NUTRITION_STEPS.COACHING;
  const isFinish = activeStep === NUTRITION_STEPS.FINISH;
  const isFirstStep = activeStep === NUTRITION_STEPS.CLIENT;

  // Enter inside a step-1 text field advances instead of submitting
  // (the create button only exists on the last step — Round 3 / Phase 20).
  const handleBodyKeyDown = (e) => {
    if (e.key !== "Enter" || !isFirstStep) return;
    const tag = e.target?.tagName;
    if (tag !== "INPUT" || e.target.type === "checkbox") return;
    e.preventDefault();
    goNext();
  };

  // Secretary without patients.createForOthers (or in a solo clinic): no
  // coach to create the client for — say so instead of a dead-end form.
  if (form.creationBlocked) {
    return (
      <div className="modal-overlay pat-wiz-overlay">
        <div className="pat-wiz-modal" role="dialog" aria-modal="true">
          <div className="pat-wiz-header">
            <div className="pat-wiz-header-actions">
              <ModalCloseButton onClick={handleSafeClose} />
            </div>
          </div>
          <NoAccessState
            size="page"
            message="כדי להוסיף לקוח/ה חדש/ה יש צורך בהרשאה ליצירת לקוחות עבור מאמנים אחרים. פנו למנהל/ת הקליניקה."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay pat-wiz-overlay">
      {isChecking && <GlobalLoader message="בודק הרשאות..." />}
      {isLoading && <GlobalLoader message="יוצר לקוח/ה..." />}
      <PlanLimitModalComponent />

      <div className="pat-wiz-modal form-container nwz-modal">
        <div className="pat-wiz-header">
          <div className="pat-wiz-title">
            <div className="pat-wiz-title-icon">
              <UserPlus size={20} weight="fill" />
            </div>
            <div className="pat-wiz-title-text">
              <span className="pat-wiz-eyebrow">לקוחות</span>
              <h3>{createdPatient ? "הלקוח/ה נוצר/ה" : "הוספת לקוח/ה חדש/ה"}</h3>
            </div>
          </div>
          <div className="pat-wiz-header-actions">
            <HelpButton featureKey="create_patient" />
            <ModalCloseButton onClick={handleSafeClose} />
          </div>
        </div>

        <div className="pat-wiz-tabs" role="tablist">
          {TABS.map((tab, index) => {
            const Icon = tab.icon;
            const isActive = activeStep === tab.id;
            const isLocked = tab.id === NUTRITION_STEPS.FINISH ? !createdPatient : !!createdPatient;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`pat-wiz-tab ${isActive ? "active" : ""} ${isLocked ? "nwz-tab-locked" : ""}`}
                onClick={() => !isLocked && goToStep(tab.id)}
                disabled={isLocked}
              >
                <span className="nwz-tab-num">{index + 1}</span>
                <Icon size={15} weight={isActive ? "fill" : "regular"} />
                <span className="pat-wiz-tab-label">{tab.label}</span>
                {tab.required && <span className="pat-wiz-tab-required">חובה</span>}
              </button>
            );
          })}
        </div>

        <div className="pat-wiz-body" onKeyDown={handleBodyKeyDown}>
          {activeStep === NUTRITION_STEPS.CLIENT && <ClientStep form={form} />}
          {activeStep === NUTRITION_STEPS.BODY && <BodyStep form={form} />}
          {activeStep === NUTRITION_STEPS.GOALS && <GoalsStep form={form} />}
          {activeStep === NUTRITION_STEPS.COACHING && <CoachingStep form={form} />}
          {isFinish && <FinishStep form={form} />}

          {errors.submit && (
            <div className="pat-wiz-submit-error">
              <Warning size={18} weight="fill" />
              <span>{errors.submit}</span>
            </div>
          )}
        </div>

        {!isFinish && (
          <div className="pat-wiz-footer nwz-footer">
            <div className="nwz-footer-nav">
              {!isFirstStep && (
                <button type="button" className="pat-wiz-btn secondary" onClick={goBack}>
                  <ArrowRight size={16} weight="bold" />
                  <span>חזרה</span>
                </button>
              )}
              {!isLastFormStep && (
                <button type="button" className="pat-wiz-btn secondary" onClick={goNext}>
                  <span>המשך</span>
                  <ArrowLeft size={16} weight="bold" />
                </button>
              )}
            </div>
            {/* The create button lives on the last step only. */}
            {isLastFormStep && (
              <button
                type="button"
                className="pat-wiz-btn primary pat-wiz-save-btn"
                onClick={handleSubmit}
                disabled={saveDisabled || !canSave}
                title={!canSave ? "שם ומספר טלפון נדרשים כדי ליצור" : undefined}
              >
                <Check size={16} weight="bold" />
                <span>{isLoading ? "יוצר..." : "צור/י לקוח/ה"}</span>
              </button>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={showCloseWarning}
        onClose={() => setShowCloseWarning(false)}
        onConfirm={() => {
          setShowCloseWarning(false);
          handleDiscardChanges();
        }}
        title="יש פרטים שלא נשמרו"
        message="לצאת בלי ליצור את הלקוח/ה? הפרטים שהוזנו יימחקו."
        confirmText="יציאה"
        cancelText="המשך עריכה"
        type="warning"
      />
    </div>
  );
}

export default NutritionCreateWizard;
